// ============================================
// AXIOS INSTANCE & API MIDDLEWARE
// Ported from old/src/api/middleware/middleware.js
// Exact same logic: token attach, refresh, error handling
// ============================================

import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { REFRESH_TOKEN, TOKEN, TOKEN_EXPIRY } from '@/utils/constants';
import { AUTH, API_METHOD, type ApiMethod } from '@/services/endpoints';
import useAuthStore from '@/store/authStore';
import { refreshTokenApi } from '@/services/auth.service';

// Create Axios instance
const axiosInstance: AxiosInstance = axios.create({
   baseURL: import.meta.env.VITE_API_BASE_URL as string,  
  // baseURL: 'http://10.10.10.73:3000/api/v1/',
  // baseURL: 'http://10.10.10.150:3003/api/v1/',
  // baseURL: 'https://kipi.tapi.sarvopariapp.com/api/v1/',

  headers: {
    'Content-Type': 'application/json',
  },
});

// Token Refresh
const refreshToken = async (): Promise<string> => {
  const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  const response = await refreshTokenApi(refreshTokenValue);
  const data = response.data;

  if (!data?.accessToken?.token) {
    throw new Error('Failed to retrieve new access token');
  }

  // Update storage & store via unified method
  useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);

  return data.accessToken.token;
};

// Handle 401 Unauthorized or Missing Token
const handleUnauthorized = async (
  error: unknown,
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  errorMessage: string,
): Promise<unknown> => {
  const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
  const isAuthRequest =
    originalRequest.url?.includes(AUTH.USER_LOGIN) ||
    originalRequest.url?.includes(AUTH.REFRESH_TOKENS);

  if (isAuthRequest) {
    if (errorMessage) toast.error(errorMessage);
    return Promise.reject(error);
  }

  if (refreshTokenValue && !originalRequest._retry) {
    originalRequest._retry = true;
    try {
      const newToken = await refreshToken();
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch {
      toast.error('Session expired. Please log in again.');
      useAuthStore.getState().logout(); // Clears everything
      window.location.href = '/login';
      return Promise.reject(error);
    }
  }

  if (window.location.pathname !== '/login') {
    toast.warning('Authentication required. Please log in.');
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

// Request Interceptor — attach token + proactive refresh + appType
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem(TOKEN);
    const expiry = localStorage.getItem(TOKEN_EXPIRY);
    const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);

    // Skip check for login and refresh tokens themselves to avoid recursion
    const isAuthRequest =
      config.url?.includes(AUTH.USER_LOGIN) || config.url?.includes(AUTH.REFRESH_TOKENS);

    if (!isAuthRequest) {
      // Proactive check: If token is expired or about to expire in 30 seconds
      if (expiry && refreshTokenValue) {
        const expiryTime = new Date(expiry).getTime();
        const currentTime = new Date().getTime();

        if (expiryTime - currentTime < 30000) {
          try {
            const newToken = await refreshToken();
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch {
            // Failure handled by interceptor logic eventually or let it 401
          }
        }
      }
    }

    if (!config.headers.Authorization && token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers.appType = 'GLOBAL_APP';
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// Response Interceptor — success toast + error handling
axiosInstance.interceptors.response.use(
  (response) => {
    const isGetRequest = response.config.method === 'get';
    const isWalletRequest = response.config.url?.includes('/wallet');
    const responseData = response.data as { message?: string };
    const hasCustomMessage =
      responseData?.message && responseData.message !== 'Institute entity retrieved successfully';

    if (!isGetRequest && hasCustomMessage && !isWalletRequest) {
      toast.success(responseData.message);
    }

    return response.data;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const errorData = error.response?.data as { message?: string } | undefined;
    const errorMessage = errorData?.message ?? error.message ?? 'Something went wrong';

    if (error.response?.status === 401) {
      return handleUnauthorized(error, originalRequest, errorMessage);
    }

    if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action.');
    } else if (error.response && error.response.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.response) {
      toast.error(errorMessage);
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  },
);

// ============================================
// Typed API Call Wrapper
// ============================================

interface ApiCallOptions {
  type: ApiMethod;
  url: string;
  body?: Record<string, any> | FormData | object;
  headers?: Record<string, string>;
  isFormData?: boolean;
}

const getUrlWithParams = (
  type: ApiMethod,
  url: string,
  body?: Record<string, unknown> | FormData,
): string => {
  if (
    (type === API_METHOD.GET || type === API_METHOD.DELETE) &&
    body &&
    !(body instanceof FormData)
  ) {
    const cleanBody = Object.fromEntries(
      Object.entries(body).filter(([, v]) => v !== null && v !== undefined && v !== ''),
    ) as Record<string, string>;
    const queryString = new URLSearchParams(cleanBody).toString();
    return queryString ? `${url}?${queryString}` : url;
  }
  return url;
};

const apiCall = async <T = unknown>({
  type,
  body,
  url,
  headers = {},
  isFormData = false,
}: ApiCallOptions): Promise<T> => {
  const finalUrl = getUrlWithParams(type, url, body as Record<string, unknown>);

  const requestConfig: AxiosRequestConfig = {
    headers: {
      ...headers,
    },
  };

  if (isFormData) {
    if (requestConfig.headers) {
      requestConfig.headers['Content-Type'] = 'multipart/form-data';
    }
  } else if (!headers['Content-Type']) {
    if (requestConfig.headers) {
      requestConfig.headers['Content-Type'] = 'application/json';
    }
  }

  switch (type) {
    case API_METHOD.POST:
      return axiosInstance.post<T>(finalUrl, body, requestConfig) as Promise<T>;
    case API_METHOD.PATCH:
      return axiosInstance.patch<T>(finalUrl, body, requestConfig) as Promise<T>;
    case API_METHOD.PUT:
      return axiosInstance.put<T>(finalUrl, body, requestConfig) as Promise<T>;
    case API_METHOD.DELETE:
      return axiosInstance.delete<T>(finalUrl, requestConfig) as Promise<T>;
    case API_METHOD.GET:
    default:
      return axiosInstance.get<T>(finalUrl, requestConfig) as Promise<T>;
  }
};

export default apiCall;
export { axiosInstance };
